import { useState } from "react";
import { useEffect } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import TimelapseRoundedIcon from "@mui/icons-material/TimelapseRounded";
import { alpha, Box, Divider, Grid, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import gtag from "ga-gtag";
import { v4 as uuid } from "uuid";

import { assessmentTemplate as omniHealthHistory } from "../../assets/assessments/omniHealthHistory";
import { createNewAssessment } from "../../data/omniDataModel";
import { getAssessmentTemplateByType } from "../../utils/helpers";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniTextField from "../ui/omniTextField";

const createNewAssessmentFromTemplate = assessmentTemplate => {
  let unsavedAssessment = createNewAssessment();
  unsavedAssessment.omniData = { ...assessmentTemplate.omniData };

  // Hackish way to clean up the data for the API
  // and to create the UUIDs we need for the assessment.
  // This only works for IF assessments right now
  // Is this what we want?

  unsavedAssessment.omniData.questions.forEach((question, index) => {
    if (question.omniID == null) {
      question.omniID = uuid();
    }
    // hydrate pronouns done in wizardPerformAssessment
  });

  if (!Object.hasOwn(unsavedAssessment.omniData.assessmentResults, "finalResult")) {
    unsavedAssessment.omniData.assessmentResults[0].algorithm[0].values.forEach((value, index) => {
      unsavedAssessment.omniData.assessmentResults[0].algorithm[0].values[index] = `${
        unsavedAssessment.omniData.questions[index].omniID
      }.${
        unsavedAssessment.omniData.assessmentResults[0].algorithm[0].values[index].split(".")[1]
      }`;
    });
  }
  return unsavedAssessment;
};

const WizardAssessmentSelection = props => {
  const { lockAssessmentOmniType = null, autoStartAssessment = false, organizationODM } = props;

  const getSupportedAssessmentTemplates = orgODM => {
    if (orgODM != null) {
      let theReturn = [];
      for (let index = 0; index < orgODM.omniData.supportedAssessmentTypes.length; ++index) {
        let aTemplate = getAssessmentTemplateByType(
          orgODM.omniData.supportedAssessmentTypes[index],
        );
        if (aTemplate) theReturn.push(aTemplate);
      }
      return theReturn;
    }

    return [omniHealthHistory];
  };

  const [myAssessmentTemplates] = useState(getSupportedAssessmentTemplates(organizationODM));
  const [lockedAssessmentTemplate] = useState(getAssessmentTemplateByType(lockAssessmentOmniType));

  const [selectedAssessmentTemplate, setSelectedAssessmentTemplate] = useState(
    lockedAssessmentTemplate ? lockedAssessmentTemplate : myAssessmentTemplates[0],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAcceptHandler = () => {
    gtag("event", "omni_assessment_selected", {
      assessmentType: selectedAssessmentTemplate.omniData.omniType,
    });
    props.setAssessmentODM(createNewAssessmentFromTemplate(selectedAssessmentTemplate));
  };

  const createFragmentHTML = text => {
    return { __html: `<Fragment>${text}</Fragment>` };
  };

  useEffect(() => {
    if (selectedAssessmentTemplate == null) {
      setSelectedAssessmentTemplate(myAssessmentTemplates[0]);
    }
  }, [selectedAssessmentTemplate, myAssessmentTemplates, setSelectedAssessmentTemplate]);

  useEffect(() => {
    if (lockedAssessmentTemplate != null && autoStartAssessment) {
      onAcceptHandler();
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedAssessmentTemplate, autoStartAssessment, onAcceptHandler]);

  const theme = useTheme();
  return (
    <Fragment>
      <DialogContent>
        <Grid container direction="row" spacing={1}>
          <Grid
            item
            xs
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            {/* Assessment Type Dropdown */}

            {lockedAssessmentTemplate == null && (
              <Grid item xs={12} md={7}>
                <OmniTextField
                  label="Select Assessment"
                  select
                  key={uuid()}
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  value={
                    !selectedAssessmentTemplate
                      ? "Select Assessment"
                      : selectedAssessmentTemplate.omniData.title
                  }
                  disabled={lockedAssessmentTemplate != null}
                  onChange={event => {
                    setSelectedAssessmentTemplate(
                      myAssessmentTemplates.find(
                        ({ omniData }) => omniData.title === event.target.value,
                      ),
                    );
                  }}
                >
                  {myAssessmentTemplates.map(assessmentTemplate => (
                    <OmniMenuItem
                      key={assessmentTemplate.omniData.title}
                      value={assessmentTemplate.omniData.title}
                    >
                      {assessmentTemplate.omniData.title}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>
            )}

            {/* Assessment Type Dropdown */}
            {lockedAssessmentTemplate != null && (
              <Grid item xs={12} md={7}>
                <Box display={"flex"} flexDirection={"column"}>
                  <Typography variant="overline" sx={{ marginBottom: `-${theme.spacing(0.5)}` }}>
                    Selected Assessment
                  </Typography>
                  <Typography variant="h5" sx={{ marginTop: `-${theme.spacing(0.5)}` }}>
                    {lockedAssessmentTemplate.omniData.title}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Icons and Such */}
            <Grid container item xs={12} md={5} justifyContent="space-between">
              <Grid item xs={5} md={12}>
                <QuestionAnswerRoundedIcon
                  fontSize="small"
                  sx={{ marginRight: theme.spacing(0.5), marginBottom: "-5px" }}
                />
                <Typography variant="button">{`${
                  selectedAssessmentTemplate.omniData.questions.length
                } Question${
                  selectedAssessmentTemplate.omniData.questions.length > 1 ? `s` : ``
                }`}</Typography>
              </Grid>
              <Grid item xs={5} md={12}>
                <TimelapseRoundedIcon
                  fontSize="small"
                  sx={{ marginRight: theme.spacing(0.5), marginBottom: "-5px" }}
                />
                <Typography variant="button">
                  {selectedAssessmentTemplate.omniData.duration}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item sm={12}>
            <Divider
              sx={{
                borderRadius: 4,
                borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                height: 1,
                width: "100%",
              }}
            />
          </Grid>
        </Grid>

        {selectedAssessmentTemplate.omniData.description && (
          <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
            <Typography variant="overline" noWrap sx={{ marginBottom: `-${theme.spacing(1)}` }}>
              description
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: "1.2rem",
                marginBottom: theme.spacing(1),
                marginLeft: theme.spacing(0.5),
              }}
              dangerouslySetInnerHTML={createFragmentHTML(
                selectedAssessmentTemplate.omniData.description,
              )}
            />
          </Box>
        )}
        {selectedAssessmentTemplate.omniData.directions && (
          <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
            <Typography variant="overline" noWrap sx={{ marginBottom: `-${theme.spacing(1)}` }}>
              directions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: "1.2rem",
                marginBottom: theme.spacing(1),
                marginLeft: theme.spacing(0.5),
              }}
              dangerouslySetInnerHTML={createFragmentHTML(
                selectedAssessmentTemplate.omniData.directions,
              )}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {
          <Button
            onClick={onAcceptHandler}
            disabled={selectedAssessmentTemplate == null}
            color="secondary"
            variant="contained"
          >
            Start
          </Button>
        }
      </DialogActions>
    </Fragment>
  );
};

const mapStateToProps = state => ({
  organizationODM: state.userSession.organizationODM,
  assessmentTemplates: state.assessmentSession.assessmentTemplates,
  assessmentTemplateODM: state.assessmentSession.assessmentTemplateODM,
});

export default connect(mapStateToProps)(WizardAssessmentSelection);
